import { cloneDeep } from 'lodash';
import type { JSONContent } from '@tiptap/core';

import { TiptapMarkType, TiptapNodeType } from '../../tiptapTypes';
import { generateTaskId } from './taskListItemIdUtils';

/**
 * Stamps a fresh `taskId` on `node` and on every task item nested beneath it, and strips any
 * `inlineComment` mark from `node` and every node nested beneath it, in place.
 */
const stampTaskIdsAndStripInlineComments = (node: JSONContent): void => {
    if (node.type === TiptapNodeType.taskItem) node.attrs = { ...node.attrs, taskId: generateTaskId() };

    if (node.marks?.some((mark) => mark.type === TiptapMarkType.inlineComment)) {
        node.marks = node.marks.filter((mark) => mark.type !== TiptapMarkType.inlineComment);
    }

    node.content?.forEach(stampTaskIdsAndStripInlineComments);
};

/**
 * Deep-copies a task item's JSON subtree, stamping a fresh `taskId` on every item within.
 *
 * Task ids are rewritten up front (rather than left to the UniqueID extension) because
 * reminder reconciliation keys rows by `(elementId, taskId)` and runs on the destination's
 * first create/update, which is too late to swap in fresh ids.
 *
 * `inlineComment` marks are stripped from the copy at every depth, so the duplicate never
 * references the source's comment thread. Duplicating comment threads is deliberately not
 * handled here.
 */
export const duplicateTaskItem = (taskItem: JSONContent): JSONContent => {
    const duplicate = cloneDeep(taskItem);

    stampTaskIdsAndStripInlineComments(duplicate);

    return duplicate;
};
